import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  Chip,
  Divider,
  Link as MuiLink,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  School as SchoolIcon,
  Email as EmailIcon,
  VpnKey as VpnKeyIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import api from '../../api/axiosConfig';

function WaitlistForm({
  error,
  email,
  allowedDomains = [],
  onInviteSuccess,
  onClose
}) {
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);

  // Waitlist join form state
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [waitlistData, setWaitlistData] = useState({
    firstName: '',
    lastName: '',
    institution: '',
    referralSource: '',
    reason: ''
  });
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;

    setLoading(true);
    setSubmitError('');

    try {
      const response = await api.post(
        '/api/auth/validate-invite-public',
        {
          email,
          inviteCode: inviteCode.trim()
        }
      );

      if (response.data.success) {
        onInviteSuccess && onInviteSuccess({ inviteCode: inviteCode.trim(), ...response.data });
      }
    } catch (error) {
      console.error('Invite validation error:', error);
      const errorData = error.response?.data;
      setSubmitError(
        errorData?.error || 'Failed to validate invite code. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitError('');

    try {
      // Build payload, only include referralSource if a value was selected
      const payload = {
        email,
        firstName: waitlistData.firstName,
        lastName: waitlistData.lastName,
        institution: waitlistData.institution,
        reason: waitlistData.reason,
        domain: email.split('@')[1]?.toLowerCase(),
        source: 'signup_form'
      };

      // Only include referralSource if user selected a value
      if (waitlistData.referralSource && waitlistData.referralSource.trim()) {
        payload.referralSource = waitlistData.referralSource;
      }

      const response = await api.post('/api/waitlist/join', payload);

      if (response.data.success) {
        setWaitlistSuccess(true);
        setShowWaitlistForm(false);
      }
    } catch (error) {
      console.error('Waitlist join error:', error);
      const errorData = error.response?.data;
      setSubmitError(
        errorData?.error || 'Failed to join waitlist. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlistDataChange = (field, value) => {
    setWaitlistData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Card sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <SchoolIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Signup is currently limited to select universities
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon fontSize="small" />
            Eligible Universities
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {allowedDomains.map((domain) => (
              <Chip
                key={domain}
                label={domain}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Students and faculty from these institutions can sign up directly
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <VpnKeyIcon fontSize="small" />
            Have an Invite Code?
          </Typography>

          {!showInviteForm ? (
            <Button
              variant="contained"
              onClick={() => setShowInviteForm(true)}
              sx={{ mb: 2 }}
            >
              Enter Invite Code
            </Button>
          ) : (
            <Box component="form" onSubmit={handleInviteSubmit} sx={{ mt: 2 }}>
              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Invite Code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Enter your invite code"
                required
                sx={{ mb: 2 }}
              />

              <Stack direction="row" spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !inviteCode.trim()}
                  fullWidth
                >
                  {loading ? 'Validating...' : 'Validate Code'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowInviteForm(false);
                    setInviteCode('');
                    setSubmitError('');
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Waitlist Join Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <PersonAddIcon fontSize="small" />
            Join the Waitlist
          </Typography>

          {waitlistSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              🎉 You've been added to the waitlist! We'll notify you when access becomes available.
            </Alert>
          ) : (
            <>
              {!showWaitlistForm ? (
                <Button
                  variant="outlined"
                  onClick={() => setShowWaitlistForm(true)}
                  sx={{ mb: 2 }}
                >
                  Join Waitlist
                </Button>
              ) : (
                <Box component="form" onSubmit={handleWaitlistSubmit} sx={{ mt: 2 }}>
                  {submitError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {submitError}
                    </Alert>
                  )}

                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="First Name"
                        value={waitlistData.firstName}
                        onChange={(e) => handleWaitlistDataChange('firstName', e.target.value)}
                        required
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Last Name"
                        value={waitlistData.lastName}
                        onChange={(e) => handleWaitlistDataChange('lastName', e.target.value)}
                        required
                        fullWidth
                        size="small"
                      />
                    </Stack>

                    <TextField
                      label="Institution/University"
                      value={waitlistData.institution}
                      onChange={(e) => handleWaitlistDataChange('institution', e.target.value)}
                      placeholder="e.g., Stanford University, Google Research"
                      fullWidth
                      size="small"
                    />

                    <FormControl fullWidth size="small">
                      <InputLabel>How did you learn about Blackmount?</InputLabel>
                      <Select
                        value={waitlistData.referralSource}
                        onChange={(e) => handleWaitlistDataChange('referralSource', e.target.value)}
                        label="How did you learn about Blackmount?"
                      >
                        <MenuItem value="friend">Friend/Colleague</MenuItem>
                        <MenuItem value="social_media">Social Media</MenuItem>
                        <MenuItem value="university">University/Professor</MenuItem>
                        <MenuItem value="search">Web Search</MenuItem>
                        <MenuItem value="conference">Conference/Event</MenuItem>
                        <MenuItem value="blog">Blog/Article</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      label="Why are you interested in Blackmount?"
                      value={waitlistData.reason}
                      onChange={(e) => handleWaitlistDataChange('reason', e.target.value)}
                      placeholder="Tell us about your research, use case, or interests..."
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                    />

                    <Stack direction="row" spacing={2}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !waitlistData.firstName || !waitlistData.lastName}
                        fullWidth
                      >
                        {loading ? 'Joining...' : 'Join Waitlist'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setShowWaitlistForm(false);
                          setWaitlistData({
                            firstName: '',
                            lastName: '',
                            institution: '',
                            referralSource: '',
                            reason: ''
                          });
                          setSubmitError('');
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Questions? Contact{' '}
            <MuiLink href="mailto:support@blackmount.ai" color="primary">
              support@blackmount.ai
            </MuiLink>
          </Typography>
        </Box>

        {onClose && (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button variant="text" onClick={onClose}>
              Back to Login
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default WaitlistForm;